<template>
  <div
    ref="container"
    class="otto-dog-container"
    :style="containerStyle"
    :tabindex="props.keyboardControl ? 0 : -1"
    :class="{ 'km-focused': props.keyboardControl && keyboard.isFocused.value }"
    @click="props.keyboardControl && (container as HTMLElement)?.focus()">
    <!-- Fallback: static image while loading or on WebGL failure -->
    <img
      v-if="!dogScene.isLoaded.value || dogScene.loadError.value"
      :src="brand.images.logoHeader"
      :alt="brand.agentName"
      class="w-full h-full object-cover"
      :class="{ 'rounded-full': props.mode === 'fab' }" />

    <!-- Keyboard control UI (only when loaded and prop enabled) -->
    <template v-if="props.keyboardControl && dogScene.isLoaded.value">
      <!-- Toggle button (top-right) -->
      <button
        class="km-toggle-btn"
        :class="{ 'km-toggle-active': !!keyboard.activeBinding.value }"
        :title="tt('Keyboard controls (? to toggle)', 'Tipkovnične bližnjice (? za preklop)')"
        @click.stop="showKeymap = !showKeymap">
        ⌨️
      </button>

      <!-- Keymap overlay panel -->
      <Transition name="km-slide">
        <div v-if="showKeymap" class="km-panel" @click.stop>
          <!-- Header -->
          <div class="km-header">
            <span class="km-title">⌨️ {{ tt('Controls', 'Tipkovnica') }}</span>
            <span v-if="keyboard.activeBinding.value" class="km-active-label">
              ▶ {{ tt(...keyboard.activeBinding.value.label) }}
            </span>
            <button class="km-close-btn" @click="showKeymap = false">✕</button>
          </div>

          <!-- Movement row: orbit d-pad + walk/run arrows + swim arrows -->
          <div class="km-move-row">
            <!-- Orbit: W/S + Left/Right arrows -->
            <div class="km-move-pad">
              <div class="km-pad-title">{{ tt('Orbit', 'Orbita') }}</div>
              <div class="km-dpad">
                <div />
                <div class="km-key">W</div>
                <div />
                <div class="km-key">←</div>
                <div class="km-dpad-dot">·</div>
                <div class="km-key">→</div>
                <div />
                <div class="km-key">S</div>
                <div />
              </div>
            </div>

            <!-- Walk / Run: ↑↓ + A/D -->
            <div class="km-move-pad">
              <div class="km-pad-title">
                {{ tt('Walk', 'Hoja') }} / <span class="km-badge">Sh</span> {{ tt('Run', 'Tek') }}
              </div>
              <div class="km-dpad">
                <div />
                <div class="km-key" :class="{ 'km-key-on': isActive('arrowup') || isActive('shift+arrowup') }">↑</div>
                <div />
                <div class="km-key" :class="{ 'km-key-on': isActive('a') || isActive('shift+a') }">A</div>
                <div class="km-dpad-dot">·</div>
                <div class="km-key" :class="{ 'km-key-on': isActive('d') || isActive('shift+d') }">D</div>
                <div />
                <div class="km-key" :class="{ 'km-key-on': isActive('arrowdown') || isActive('shift+arrowdown') }">
                  ↓
                </div>
                <div />
              </div>
            </div>

            <!-- Swim: Alt+Arrow -->
            <div class="km-move-pad">
              <div class="km-pad-title">{{ tt('Swim', 'Plavanje') }} <span class="km-badge">Alt</span></div>
              <div class="km-dpad">
                <div />
                <div class="km-key km-key-sm" :class="{ 'km-key-on': isActive('alt+arrowup') }">↑</div>
                <div />
                <div class="km-key km-key-sm" :class="{ 'km-key-on': isActive('alt+arrowleft') }">←</div>
                <div class="km-dpad-dot">·</div>
                <div class="km-key km-key-sm" :class="{ 'km-key-on': isActive('alt+arrowright') }">→</div>
                <div />
                <div class="km-key km-key-sm" :class="{ 'km-key-on': isActive('alt+arrowdown') }">↓</div>
                <div />
              </div>
            </div>
          </div>

          <!-- Action chips -->
          <div class="km-section-title">{{ tt('Actions', 'Dejanja') }}</div>
          <div class="km-chips">
            <div v-for="b in actionBindings" :key="b.id" class="km-chip" :class="{ 'km-chip-on': isActive(b.id) }">
              <span class="km-chip-key">{{ b.display }}</span>
              <span class="km-chip-lbl">{{ tt(...b.label) }}</span>
            </div>
          </div>

          <div class="km-footer">{{ tt('Press', 'Pritisni') }} <kbd>?</kbd> {{ tt('to toggle', 'za preklop') }}</div>
        </div>
      </Transition>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, toRef } from 'vue';
import { useOttoDog, type OttoAnimation, type OttoCameraMode } from '../composables/useOttoDog';
import { brand } from '../config/brand';
import { useOttoDogKeyboard, KEY_BINDINGS } from '../composables/useOttoDogKeyboard';
import { playOttoSound } from '../utils/ottoSounds';
import { tt } from '../utils/consoleText';
import { bimCameraAzimuth } from '../composables/useBimScene';

const props = withDefaults(
  defineProps<{
    mode?: OttoCameraMode;
    animation?: OttoAnimation;
    width?: number;
    height?: number;
    keyboardControl?: boolean;
  }>(),
  {
    mode: 'fab',
    animation: 'idle',
    width: 48,
    height: 48,
    keyboardControl: false,
  },
);

const container = ref<HTMLElement | null>(null);
const modeRef = toRef(props, 'mode');
const showKeymap = ref(false);

const containerStyle = computed(() => ({
  width: `${props.width}px`,
  height: `${props.height}px`,
}));

const dogScene = useOttoDog(() => container.value, modeRef);

// ── Keyboard control ─────────────────────────────────────────────
function returnToIdle() {
  const clips = dogScene.availableClips.value;
  // Exact name matches (priority order)
  for (const name of ['IdleA', 'IdleB', 'IdleC', 'Idle_A', 'Rest_Pose']) {
    if (clips.includes(name)) {
      dogScene.playClipByName(name);
      return;
    }
  }
  // Case-insensitive fuzzy fallback
  const fuzzy = clips.find((n) => n.toLowerCase().includes('idle'));
  if (fuzzy) {
    dogScene.playClipByName(fuzzy);
    return;
  }
  // Absolute last resort: first available clip
  if (clips[0]) dogScene.playClipByName(clips[0]);
}

// Track whether dog is currently in a movement animation
let _isMoving = false;

const keyboard = useOttoDogKeyboard(
  (clipName) => {
    if (clipName === '__hey_otto__') {
      playOttoSound('hey_otto');
      dogScene.resetView();
      _isMoving = false;
      returnToIdle();
      return;
    }
    const binding = KEY_BINDINGS.find((b) => b.clipName === clipName);
    const isMovement = binding?.group === 'walk' || binding?.group === 'run' || binding?.group === 'swim';
    if (isMovement && !_isMoving) {
      // First movement key from idle — align dog to face BIM camera direction
      dogScene.setFacingAngle(bimCameraAzimuth.value);
    }
    _isMoving = isMovement;
    dogScene.playClipByName(clipName);
  },
  () => {
    _isMoving = false;
    returnToIdle();
  },
  (key, pressed) => dogScene.orbitKey(key, pressed),
);

const actionBindings = computed(() => KEY_BINDINGS.filter((b) => b.group === 'action'));

function isActive(keyId: string): boolean {
  return keyboard.activeBinding.value?.id === keyId;
}

// Activate / deactivate keyboard when prop changes
watch(
  () => props.keyboardControl,
  (enabled) => {
    if (enabled) {
      keyboard.activate(container.value);
    } else {
      keyboard.deactivate();
      showKeymap.value = false;
    }
  },
);

// ── Expose scene methods to parent ───────────────────────────────
defineExpose({
  playClipByName: dogScene.playClipByName,
  availableClips: dogScene.availableClips,
  resume: dogScene.resume,
  isLoaded: dogScene.isLoaded,
});

// Watch animation prop and forward to scene
watch(
  () => props.animation,
  (anim) => {
    dogScene.setAnimation(anim);
  },
);

// Visibility observer — pause when off-screen
let visibilityObs: IntersectionObserver | null = null;
let resizeObs: ResizeObserver | null = null;

// '?' key toggles keymap; Escape closes it — fired on the container element
function onMetaKey(e: KeyboardEvent) {
  if (!props.keyboardControl) return;
  if (e.key === '?') {
    showKeymap.value = !showKeymap.value;
    e.preventDefault();
  } else if (e.key === 'Escape' && showKeymap.value) {
    showKeymap.value = false;
    e.preventDefault();
  }
}

onMounted(() => {
  dogScene.init();

  if (props.keyboardControl) keyboard.activate(container.value);
  container.value?.addEventListener('keydown', onMetaKey);

  // Pause render when not visible
  if (container.value) {
    visibilityObs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          dogScene.resume();
        } else {
          dogScene.pause();
        }
      },
      { threshold: 0.1 },
    );
    visibilityObs.observe(container.value);

    resizeObs = new ResizeObserver(() => {
      dogScene.resize();
    });
    resizeObs.observe(container.value);
  }
});

onBeforeUnmount(() => {
  container.value?.removeEventListener('keydown', onMetaKey);
  visibilityObs?.disconnect();
  resizeObs?.disconnect();
});
</script>

<style scoped>
.otto-dog-container {
  position: relative;
  overflow: hidden;
  line-height: 0;
  outline: none; /* custom focus ring below */
}

/* Teal focus ring when keyboard-controlled and focused */
.otto-dog-container.km-focused {
  outline: 2px solid rgba(99, 202, 183, 0.7);
  outline-offset: 2px;
}

/* Keyboard focus hint overlay */
.km-focus-hint {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 10px;
  pointer-events: none;
  z-index: 10;
}
.km-focus-hint span {
  background: rgba(0, 0, 0, 0.55);
  color: #cbd5e1;
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  white-space: nowrap;
}

.otto-dog-container canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100% !important;
  height: 100% !important;
}

/* ── Keyboard toggle button ──────────────────────────────────────── */
.km-toggle-btn {
  position: absolute;
  top: 7px;
  right: 7px;
  z-index: 20;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: white;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.15s,
    transform 0.1s;
  line-height: 1;
}
.km-toggle-btn:hover {
  background: rgba(0, 0, 0, 0.7);
}
.km-toggle-active {
  background: rgba(99, 202, 183, 0.5) !important;
}

/* ── Overlay panel ────────────────────────────────────────────────── */
.km-panel {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 15;
  background: rgba(8, 12, 24, 0.91);
  backdrop-filter: blur(10px);
  color: #cbd5e1;
  font-size: 10.5px;
  padding: 8px 10px 6px;
  max-height: 75%;
  overflow-y: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* Slide transition */
.km-slide-enter-active,
.km-slide-leave-active {
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
}
.km-slide-enter-from,
.km-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

/* ── Panel header ────────────────────────────────────────────────── */
.km-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 7px;
}
.km-title {
  font-weight: 600;
  font-size: 11px;
  color: #e2e8f0;
  flex-shrink: 0;
}
.km-active-label {
  flex: 1;
  color: #63cab7;
  font-size: 10px;
  /* `.otto-dog-container` carries line-height: 0 (it kills the inline gap under
     the canvas) and this box inherits it.  That is harmless for the other text
     in the panel — overflow stays visible and their glyphs paint anyway — but
     this one sets `overflow: hidden` for its ellipsis, so at line-height 0 it
     clips its own text away and the `▶ <binding>` line never paints.  Give it a
     real line box; 1.2 at 10px is the 12px the header already measures. */
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.km-close-btn {
  margin-left: auto;
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  font-size: 12px;
  padding: 0 2px;
  line-height: 1;
  flex-shrink: 0;
}
.km-close-btn:hover {
  color: #e2e8f0;
}

/* ── Movement row ─────────────────────────────────────────────────── */
.km-move-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 8px;
}
.km-move-pad {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
}
.km-pad-title {
  font-size: 9.5px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* D-pad grid (3×3) */
.km-dpad {
  display: grid;
  grid-template-columns: repeat(3, 22px);
  grid-template-rows: repeat(3, 22px);
  gap: 2px;
}
.km-dpad-dot {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  font-size: 14px;
}

/* Keys */
.km-key {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.07);
  font-size: 13px;
  cursor: default;
  transition:
    background 0.08s,
    border-color 0.08s,
    color 0.08s;
  color: #cbd5e1;
}
.km-key-sm {
  font-size: 10px;
}
.km-key-on {
  background: rgba(99, 202, 183, 0.35);
  border-color: #63cab7;
  color: #fff;
  box-shadow: 0 0 6px rgba(99, 202, 183, 0.5);
}

/* ── Badge (e.g. "Ctrl") ─────────────────────────────────────────── */
.km-badge {
  font-size: 8px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  padding: 0 3px;
  color: #94a3b8;
  line-height: 1.5;
}

/* ── Chip column (swim) ───────────────────────────────────────────── */
.km-chips-col {
  flex-direction: column !important;
  gap: 3px !important;
}

/* ── Action chips ────────────────────────────────────────────────── */
.km-section-title {
  font-size: 9.5px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  margin-bottom: 4px;
}
.km-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  margin-bottom: 6px;
}
.km-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 5px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  white-space: nowrap;
  cursor: default;
  transition:
    background 0.08s,
    border-color 0.08s;
}
.km-chip-on {
  background: rgba(99, 202, 183, 0.25);
  border-color: #63cab7;
  color: #e2e8f0;
}
.km-chip-key {
  font-family: monospace;
  font-size: 9.5px;
  background: rgba(255, 255, 255, 0.12);
  padding: 0 3px;
  border-radius: 3px;
  color: #e2e8f0;
}
.km-chip-lbl {
  font-size: 9.5px;
  color: #94a3b8;
}
.km-chip-on .km-chip-lbl {
  color: #cbd5e1;
}

/* ── Footer hint ─────────────────────────────────────────────────── */
.km-footer {
  font-size: 9px;
  color: #475569;
  text-align: center;
  margin-top: 2px;
}
.km-footer kbd {
  font-family: monospace;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  padding: 0 3px;
  font-size: 9px;
}
</style>
